import { css } from '@emotion/react';
import type { SxProps } from '@mui/material';
import { Menu as MUIMenu, MenuItem as MUIMenuItem } from '@mui/material';
import type {
	ComponentPropsWithoutRef,
	Dispatch,
	HTMLAttributes,
	MouseEventHandler,
	ReactNode,
	RefObject,
	SetStateAction,
} from 'react';
import {
	createContext,
	createRef,
	forwardRef,
	useContext,
	useRef,
	useState,
} from 'react';
import type { To } from 'react-router';
import { Link } from 'react-router';

import { su, theme, typography } from '@adflow/frontend/ui-theme';
import { useForwardingRef } from '@adflow/frontend/util-common';

import { DropdownTransition } from './DropdownTransition';
import { HubTooltip } from './HubTooltip';

export const menuItemPadding = '0.5rem 0.6rem';

export const menuButtonStyles = {
	/** Styles for both MenuList and MenuItems */
	menuList: css(
		{
			backgroundColor: theme.colors.white,
		},
		su.roundedSm,
		{
			borderColor: theme.colors.input.focusedBorder,
			overflow: 'auto',
			borderWidth: '1px',
			borderStyle: 'solid',
			outline: '2px solid transparent',
			outlineOffset: '2px',

			maxHeight: 'calc(100vh - 12rem)',
			boxShadow: '0px 4px 8px #0000001f',
		}
	),

	/** Style for both MenuItem and MenuLink */
	menuItem: css(typography.regular, {
		padding: menuItemPadding,
		'&:hover, &[data-selected]': {
			backgroundColor: theme.colors.background.dropdown.hoverItemBackground,
		},

		// Show that MenuLink and MenuItem are clickable.
		cursor: 'pointer',

		'&[data-disabled=true]': css({
			cursor: 'default',
			color: theme.colors.text.hint,
		}),
		// Made MenuLink display the same as MenuItem.
		// Because MenuLink renders a `<a />`, by default
		// it has `display: inline` which messes up the sizing.
		display: 'block',
	}),
};

export const muiMenuStyles: Record<string, SxProps> = {
	/** Styles for both MenuList and MenuItems */
	menuList: {
		backgroundColor: theme.colors.white,
		borderRadius: '0.125rem',
		borderColor: theme.colors.input.focusedBorder,
		overflow: 'auto',
		borderWidth: '1px',
		borderStyle: 'solid',
		outline: '2px solid transparent',
		outlineOffset: '2px',
		maxHeight: 'calc(100vh - 12rem)',
		paddingTop: 0,
		paddingBottom: 0,
	},
	menuPaper: {
		boxShadow: '0px 4px 8px #0c09091f !important',
	},
	/** Style for both MenuItem and MenuLink */
	menuItem: {
		fontFamily: theme.fontFamily.sans,
		fontSize: '0.875rem',
		lineHeight: '1.25rem',
		color: theme.colors.text.primary,
		minHeight: 'auto',
		padding: menuItemPadding,
		'&:hover, &[data-selected]': {
			backgroundColor: theme.colors.background.dropdown.hoverItemBackground,
		},
		// Show that MenuLink and MenuItem are clickable.
		cursor: 'pointer',

		'&[data-disabled=true]': {
			cursor: 'default',
			color: theme.colors.text.hint,
		},
		// Made MenuLink display the same as MenuItem.
		// Because MenuLink renders a `<a />`, by default
		// it has `display: inline` which messes up the sizing.
		display: 'block',
		fontWeight: 400,
	},
};

interface MenuContextProps {
	open: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
	anchorEl: RefObject<HTMLButtonElement>;
}

const MenuContext = createContext<MenuContextProps>({
	open: false,
	setOpen: () => undefined,
	anchorEl: createRef<HTMLButtonElement>(),
});

export const useMenuContext = (): MenuContextProps => {
	const context = useContext(MenuContext);
	if (context === null) {
		throw new Error('Could not get menu context. Did you forget to use Menu?');
	}

	return context;
};

export const MenuButton = forwardRef<
	HTMLButtonElement,
	ComponentPropsWithoutRef<'button'>
>(function MenuButtonRender({ children, ...props }, ref) {
	const { open, setOpen, anchorEl } = useContext(MenuContext);

	const buttonRef = useForwardingRef(null, [ref, anchorEl]);

	return (
		<button
			type="button"
			{...props}
			ref={buttonRef}
			onClick={(e) => {
				props.onClick?.(e);
				// @ts-expect-error `data-*` attributes are not in the type definition
				if (props['data-disabled']) return;
				setOpen(!open);
			}}
		>
			{children}
		</button>
	);
});

interface MenuProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
	trigger: ReactNode;
	children:
		| ReactNode
		| (({
				isExpanded,
				setOpen,
		  }: {
				isExpanded: boolean;
				setOpen: Dispatch<SetStateAction<boolean>>;
		  }) => ReactNode);
}

export function Menu({ children, trigger, ...props }: MenuProps) {
	const anchorEl = useRef<HTMLButtonElement>(null);
	const [open, setOpen] = useState(false);

	return (
		<MenuContext.Provider
			value={{
				open,
				setOpen,
				anchorEl,
			}}
		>
			{trigger}
			<MUIMenu
				{...props}
				anchorEl={anchorEl.current}
				onClose={() => setOpen(false)}
				open={open}
				disableAutoFocus
				PaperProps={{
					sx: muiMenuStyles.menuPaper,
				}}
				MenuListProps={{
					sx: muiMenuStyles.menuList,
					disablePadding: true,
				}}
				TransitionComponent={DropdownTransition}
			>
				{typeof children === 'function'
					? children({ isExpanded: open, setOpen })
					: children}
			</MUIMenu>
		</MenuContext.Provider>
	);
}

interface MenuItemProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	disabled?: boolean;
	onDisabledClick?: MouseEventHandler<HTMLDivElement>;
	onSelect?: () => void;
}

export const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
	function MenuItemRender(
		{ children, onDisabledClick, disabled, ...props },
		ref
	) {
		const { setOpen } = useContext(MenuContext);

		return (
			<MUIMenuItem
				{...props}
				ref={ref}
				sx={muiMenuStyles.menuItem}
				onClick={(e) => {
					if (disabled) {
						onDisabledClick?.(e);
						return;
					}
					props.onSelect?.();
					setOpen(false);
				}}
				data-disabled={disabled}
				component="div"
				disableRipple
			>
				{children}
			</MUIMenuItem>
		);
	}
);

interface MenuLinkProps extends HTMLAttributes<HTMLAnchorElement> {
	to: To;
	children: ReactNode;
	disabled?: boolean;
	disabledCalloutText?: string;
	onDisabledClick?: MouseEventHandler<HTMLAnchorElement>;
}

export function MenuLink({
	to,
	children,
	disabledCalloutText,
	onDisabledClick,
	disabled,
	onClick,
	...props
}: MenuLinkProps) {
	const { setOpen } = useContext(MenuContext);

	return (
		<HubTooltip title={disabled ? disabledCalloutText : null}>
			<MUIMenuItem
				{...props}
				sx={muiMenuStyles.menuItem}
				onClick={(e) => {
					if (disabled) {
						onDisabledClick?.(e);
						e.preventDefault();
						return;
					}
					onClick?.(e);
					setOpen(false);
				}}
				component={Link}
				to={to}
				data-disabled={disabled}
				disableRipple
			>
				{children}
			</MUIMenuItem>
		</HubTooltip>
	);
}

export const MenuList = 'div';
export const MenuPopover = MenuList;
